import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

export interface AuthUser {
  id: string;
  email: string;
  displayName: string | null;
  tenantId: string;
  /** Mirror of the backend `User.platformAdmin` flag. Drives the
   *  visibility of the admin-only Economics page in nav. The backend
   *  re-checks on every `admin/*` request — this is a UX optimisation,
   *  not a security boundary. */
  platformAdmin?: boolean;
  /** Per ADR-0041 / philosophy §1.1. NULL = "not yet asked" — the SPA
   *  shows a channel-pick screen on first Copilot open. 'text' | 'voice'
   *  once the user has picked. The persistent header toggle writes
   *  through `PATCH /v1/me/assistant-channel`. */
  assistantChannel?: 'text' | 'voice' | null;
}

export interface DevShim {
  tenantId: string;
  userId: string;
}

const STORAGE_KEY = 'avp.auth.v1';

interface PersistedAuth {
  accessToken?: string;
  refreshToken?: string;
  user?: AuthUser;
  devShim?: DevShim;
}

/**
 * Auth state for the SPA. Two mutually-exclusive modes:
 *
 *  - Bearer flow (passwordless email-code, per ADR-0024): `accessToken`
 *    is set after `/v1/auth/verify-code` returns. Axios attaches it as
 *    `Authorization: Bearer ...`. Refresh-token rotation lives in the
 *    response interceptor on the api client.
 *  - Dev shim: `devShim` carries the tenant id + user id of an existing
 *    DB row. Axios sends them as `x-tenant-id` / `x-user-id`. The core
 *    backend ignores those headers when NODE_ENV === 'production'.
 *
 * State is mirrored to localStorage so a hard refresh keeps you logged
 * in. Long-term we should move to httpOnly cookies set by core (per
 * docs/09-auth-multitenancy.md) — tracked as known debt for the auth
 * module.
 */
export const useAuthStore = defineStore('auth', () => {
  const accessToken = ref<string | undefined>();
  const refreshToken = ref<string | undefined>();
  const user = ref<AuthUser | undefined>();
  const devShim = ref<DevShim | undefined>();

  const isAuthenticated = computed(
    () => Boolean(accessToken.value) || Boolean(devShim.value),
  );

  const isPlatformAdmin = computed(() => user.value?.platformAdmin === true);

  function persist(): void {
    const snapshot: PersistedAuth = {};
    if (accessToken.value) snapshot.accessToken = accessToken.value;
    if (refreshToken.value) snapshot.refreshToken = refreshToken.value;
    if (user.value) snapshot.user = user.value;
    if (devShim.value) snapshot.devShim = devShim.value;
    if (Object.keys(snapshot).length === 0) {
      window.localStorage.removeItem(STORAGE_KEY);
    } else {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot));
    }
  }

  function restoreFromStorage(): void {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    try {
      const parsed = JSON.parse(raw) as PersistedAuth;
      accessToken.value = parsed.accessToken;
      refreshToken.value = parsed.refreshToken;
      user.value = parsed.user;
      devShim.value = parsed.devShim;
    } catch {
      window.localStorage.removeItem(STORAGE_KEY);
    }
  }

  /**
   * Stores the result of a successful `/v1/auth/verify-code` or
   * `/v1/auth/refresh` response.
   */
  function setFromSession(payload: {
    accessToken: string;
    refreshToken: string;
    user: AuthUser;
  }): void {
    accessToken.value = payload.accessToken;
    refreshToken.value = payload.refreshToken;
    user.value = payload.user;
    devShim.value = undefined;
    persist();
  }

  function setDevShim(shim: DevShim, fakeUser?: AuthUser): void {
    devShim.value = shim;
    accessToken.value = undefined;
    refreshToken.value = undefined;
    user.value = fakeUser;
    persist();
  }

  function setUser(next: AuthUser | undefined): void {
    user.value = next;
    persist();
  }

  function clear(): void {
    accessToken.value = undefined;
    refreshToken.value = undefined;
    user.value = undefined;
    devShim.value = undefined;
    persist();
  }

  return {
    accessToken,
    refreshToken,
    user,
    devShim,
    isAuthenticated,
    isPlatformAdmin,
    restoreFromStorage,
    setFromSession,
    setDevShim,
    setUser,
    clear,
  };
});
